import type {
  ActivityResponse,
  AdminSetPasswordOut,
  AnimalCreateOut,
  AnimalOut,
  AnimalPatchOut,
  AnimalWeightDeleteOut,
  AnimalWeightOut,
  AssignmentDeleteOut,
  AssignmentOut,
  AssignmentSplitOut,
  AssignmentSplitPieceIn,
  AttachmentDeleteOut,
  AttachmentOut,
  BidOut,
  CadenceOut,
  CadencePreviewOut,
  CadenceScheduleDetail,
  CadenceTypeValue,
  CompletionOut,
  CompletionRateResponse,
  CompletionUndoOut,
  ContactArchiveOut,
  ContactCreateOut,
  ContactOut,
  ContactPatchOut,
  ContactRestoreOut,
  CredentialsSetupOut,
  DueResponse,
  HealthEventOut,
  HealthEventTypeValue,
  HealthLogResponse,
  LoginOut,
  LogoutOut,
  MagicLinkIssueOut,
  MagicLinkListItemOut,
  MagicLinkRevokeOut,
  MeOut,
  MyProjectTaskOut,
  OverdueAgingResponse,
  PasskeyDeleteOut,
  PasskeyLoginOut,
  PasskeyOut,
  PasswordChangeOut,
  PasswordResetOut,
  ProjectArchiveOut,
  ProjectCreateOut,
  ProjectOut,
  ProjectPatchOut,
  ProjectRestoreOut,
  ProjectShareCreateOut,
  ProjectShareDeleteOut,
  ProjectShareOut,
  ProjectTaskArchiveOut,
  ProjectTaskCreateOut,
  ProjectTaskOut,
  ProjectTaskPatchOut,
  PushSubscriptionOut,
  PushSubscriptionRevokeOut,
  PushTestOut,
  RemindersPrefsOut,
  ScheduleExceptionActionValue,
  ScheduleExceptionDeleteOut,
  ScheduleExceptionOutFull,
  ScheduleResponse,
  SetPrimaryPhotoOut,
  SettingsOut,
  SettingsPatchOut,
  ShareableUserOut,
  TeamBoardOut,
  UserAuthStatusOut,
  UserOut,
  VapidPublicKeyOut,
  WeatherOut,
  WorkloadResponse,
} from "../types";
import { clearCachedIdentity } from "./meCache";

// Same-origin relative paths — the Vite dev server proxies /api to
// localhost:8088 (vite.config.ts), and in production FastAPI serves both
// the built PWA and /api from one origin (ADR-002 §3, no CORS needed).
const API_BASE = "/api/v1";

// Sprint-003 A5 — custom-header CSRF defense (app/api/v1/deps.py
// ::require_csrf, app/config.py's Settings.csrf_header_name). Any
// non-empty value satisfies the server check — the point is that a
// cross-site request cannot set a custom header, not this specific
// value. A no-op for GET/HEAD (the server only checks unsafe methods and
// only when a session cookie is in play), so it's safe to always send.
const CSRF_HEADER_NAME = "X-Requested-With";
const CSRF_HEADER_VALUE = "hayloft-pwa";

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * ADR-002 Amendment 12 §1.6 — the ONE structured-`detail` discriminator this
 * app has. `app/api/v1/deps.py::_enforce_reset_scope` answers **403** with
 * `{"detail": {"code": "password_reset_required", "message": ...}}` on every
 * route a reset-required principal touches outside the three-entry allowlist
 * (`GET /me`, `POST /auth/password/reset`, `POST /auth/logout`).
 *
 * Every OTHER authz 403 in this codebase returns a FLAT STRING detail
 * ("not permitted" from `require_role`/`deny`, "missing CSRF header" from
 * `require_csrf`), so a `detail` OBJECT carrying a `code` key is
 * unambiguously distinguishable with zero change to any existing 403. That
 * asymmetry is the contract — do not add a second signal (no response
 * header, no status change).
 */
export const RESET_REQUIRED_CODE = "password_reset_required";

/**
 * Pulls `detail.code` out of an error body when — and only when — `detail`
 * is a JSON object. A flat-string `detail` (every ordinary authz 403) and a
 * non-JSON body (an HTML error page from a proxy) both yield `null`, so an
 * ordinary 403 can never be mistaken for a reset-scope refusal.
 *
 * FastAPI's own 422 `detail` is an ARRAY of validation errors — excluded by
 * the `Array.isArray` guard rather than left to chance, since an array's
 * `.code` is `undefined` but the intent should be explicit.
 */
function parseDetailCode(body: string): string | null {
  if (!body) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const detail = (parsed as { detail?: unknown }).detail;
  if (typeof detail !== "object" || detail === null || Array.isArray(detail)) return null;
  const code = (detail as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}

export class ApiError extends Error {
  status: number;
  /** True for a genuinely unreachable network (offline, DNS, timeout) —
   * the caller should queue and retry. False for an HTTP error response
   * (4xx/5xx) that reached the server and will not resolve on retry. */
  isNetworkError: boolean;
  /** ADR-002 Amendment 12 §1.6 / Phase A task A6 — the parsed
   * `detail.code` when the server sent a STRUCTURED detail object, else
   * `null`. Today the only value that ever appears is
   * `RESET_REQUIRED_CODE`; callers branch on it (`app.tsx`) so a
   * reset-scoped 403 routes the person to the forced-change screen instead
   * of rendering a generic "Forbidden". */
  code: string | null;

  constructor(
    message: string,
    status: number,
    isNetworkError: boolean,
    code: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.isNetworkError = isNetworkError;
    this.code = code;
  }
}

/** True iff this error is the reset-scope refusal of Amendment 12 §1.6 —
 * a live, valid session whose principal is temporarily out of scope, NOT an
 * authorization failure. Never render "Forbidden" for one of these. */
export function isResetRequiredError(err: unknown): boolean {
  return err instanceof ApiError && err.code === RESET_REQUIRED_CODE;
}

// --- Reset-scope refusal broadcast -----------------------------------------
//
// The refusal can arrive from ANY hook's fetch (a service-worker replay, a
// poll that was already in flight when the flag landed, a stale mount), and
// UX §6.1 B2 requires that it never surface as a per-hook error string. One
// module-level observer keeps that promise in ONE place instead of asking
// every hook to remember: `app.tsx` subscribes, and the routing decision
// lives there — exactly where SA task D4 put it.

type ResetRequiredListener = () => void;

const resetRequiredListeners = new Set<ResetRequiredListener>();

/** Subscribe to reset-scope refusals. Returns an unsubscribe function. */
export function onResetRequired(listener: ResetRequiredListener): () => void {
  resetRequiredListeners.add(listener);
  return () => {
    resetRequiredListeners.delete(listener);
  };
}

function announceResetRequired(code: string | null): void {
  if (code !== RESET_REQUIRED_CODE) return;
  // Snapshot: a listener may unsubscribe itself while we're iterating.
  for (const listener of [...resetRequiredListeners]) listener();
}

/** Side effects every non-2xx response from our own API shares. Any 401
 * means this device's session is gone, so the offline identity cache
 * (`lib/meCache.ts`) must not outlive it: the next cold start with no
 * signal shows "Couldn't connect" rather than reopening the app as a
 * person who is no longer signed in. */
function noteHttpError(status: number, code: string | null): void {
  announceResetRequired(code);
  if (status === 401) clearCachedIdentity();
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (UNSAFE_METHODS.has(method)) headers[CSRF_HEADER_NAME] = CSRF_HEADER_VALUE;

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { ...headers, ...init?.headers },
    });
  } catch (err) {
    // fetch() throws (TypeError) for network failures — offline, DNS,
    // connection refused. This is the "queue it for later" case.
    throw new ApiError(err instanceof Error ? err.message : "network error", 0, true);
  }

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    const code = parseDetailCode(body);
    noteHttpError(response.status, code);
    throw new ApiError(`${response.status} ${body}`.trim(), response.status, false, code);
  }

  // Some endpoints (e.g. logout) return a 200 with an empty/void body in
  // rare edge cases — guard against `response.json()` throwing on empty text.
  const text = await response.text();
  return (text ? JSON.parse(text) : (undefined as T)) as T;
}

export function fetchDue(date?: string, scope?: "mine" | "team"): Promise<DueResponse> {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  if (scope) params.set("scope", scope);
  const query = params.toString();
  return request<DueResponse>(`/due${query ? `?${query}` : ""}`);
}

// ---------------------------------------------------------------------------
// Sprint-003 A5 — session bootstrap + logout
// ---------------------------------------------------------------------------

/**
 * `GET /api/v1/me` — the current session's user. Rejects with
 * `ApiError(status: 401)` when there's no valid session cookie (the
 * "ask your farm admin for your link" logged-out state — see `hooks/useMe.ts`).
 */
export function fetchMe(): Promise<MeOut> {
  return request<MeOut>("/me");
}

/** `POST /api/v1/auth/logout` — revokes the current session, clears the
 * cookie server-side. Idempotent; a cookie-authed mutation, so the CSRF
 * header applies (handled generically by `request()` above). */
export function postLogout(): Promise<LogoutOut> {
  return request<LogoutOut>("/auth/logout", { method: "POST", body: JSON.stringify({}) });
}

// ---------------------------------------------------------------------------
// Sprint-007 D4 (ADR-002 Amendment 4 §7) — auth v2 password core: returning
// login, first-login onboarding, and recovery/reset. All three are called
// from full-page pre-app screens (`views/LoginView.tsx`,
// `views/CredentialSetupView.tsx`), never from inside `AuthenticatedApp`.
// `POST /auth/login` is pre-session (no cookie yet) — `request()`'s CSRF
// header is still sent (harmless; the server no-ops `require_csrf` when no
// session cookie is present, see `app/api/v1/auth.py`'s module docstring)
// so no special-casing is needed here.
// ---------------------------------------------------------------------------

export interface LoginBody {
  username: string;
  password: string;
}

/** `POST /api/v1/auth/login` — verifies `{username, password}` and mints a
 * fresh session cookie on success. Throws `ApiError(status: 401)` for
 * ANY rejection reason (unknown username, no credentials yet, wrong
 * password — all indistinguishable by design, no user enumeration) and
 * `ApiError(status: 429)` when the per-IP or per-username throttle trips. */
export function postLogin(body: LoginBody): Promise<LoginOut> {
  return request<LoginOut>("/auth/login", { method: "POST", body: JSON.stringify(body) });
}

export interface CredentialsSetupBody {
  username: string;
  password: string;
}

/** `POST /api/v1/auth/credentials/setup` — first-login onboarding. Session-
 * authed (the magic-link consume already minted the session); only
 * reachable while `useMe`'s `needsOnboarding` is true. Writes
 * `users.username` + the user's one `user_credentials` row. */
export function postCredentialsSetup(body: CredentialsSetupBody): Promise<CredentialsSetupOut> {
  return request<CredentialsSetupOut>("/auth/credentials/setup", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface PasswordResetBody {
  new_password: string;
}

/** `POST /api/v1/auth/password/reset` — recovery path. Session-authed;
 * only reachable while `useMe`'s `passwordResetRequired` is true. No
 * current password required — the freshly-consumed admin-issued magic
 * link is itself the proof (ADR-002 A4 §5). */
export function postPasswordReset(body: PasswordResetBody): Promise<PasswordResetOut> {
  return request<PasswordResetOut>("/auth/password/reset", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface PasswordChangeBody {
  current_password: string;
  new_password: string;
}

/** `POST /api/v1/auth/password/change` — the Settings "Your sign-in"
 * change-password sheet (UX §4.2, ADR-002 A4 §5 — REQUIRES the current
 * password, unlike `postPasswordReset` above). Throws `ApiError(status:
 * 401)` on a wrong current password (generic "that didn't match" copy —
 * `lib/authCopy.ts::SETTINGS_CURRENT_PASSWORD_MISMATCH_ERROR`). */
export function postPasswordChange(body: PasswordChangeBody): Promise<PasswordChangeOut> {
  return request<PasswordChangeOut>("/auth/password/change", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Sprint-007 D6 — passkeys (WebAuthn, ADR-002 Amendment 4 decision 3,
// mirrors app/api/v1/auth.py's passkey routes). The `.../begin` endpoints
// return the literal WebAuthn options JSON `lib/webauthn.ts` converts into
// browser `CredentialCreationOptions`/`CredentialRequestOptions`; the
// `.../complete` endpoints accept the literal `PublicKeyCredential` JSON
// `lib/webauthn.ts` serializes back out — both typed as `Record<string,
// unknown>` here rather than hand-modeled, matching `app/schemas/
// auth.py`'s own passthrough-typing rationale for these four endpoints.
// Every call here goes through `lib/webauthn.ts`'s high-level
// `enrollPasskey()`/`signInWithPasskey()` — no view calls these directly.
// ---------------------------------------------------------------------------

/** `POST /api/v1/auth/passkeys/register/begin` — session-authed. Neither
 * endpoint declares a request-body model server-side (see
 * `app/api/v1/auth.py`); sending `{}` mirrors `postLogout`'s existing
 * precedent for this codebase's other bodyless mutating calls. */
export function postPasskeyRegisterBegin(): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>("/auth/passkeys/register/begin", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export interface PasskeyRegisterCompleteBody {
  credential: Record<string, unknown>;
  nickname?: string | null;
}

export function postPasskeyRegisterComplete(
  body: PasskeyRegisterCompleteBody,
): Promise<PasskeyOut> {
  return request<PasskeyOut>("/auth/passkeys/register/complete", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** `POST /api/v1/auth/passkeys/login/begin` — PRE-session, reachable with
 * no cookie at all (exactly like `postLogin`). No username in the body —
 * `allow_credentials` comes back empty so the browser's own
 * conditional-UI/discoverable-credential flow decides which passkey to
 * offer (ADR-002 A4 decision 4b). */
export function postPasskeyLoginBegin(): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>("/auth/passkeys/login/begin", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export interface PasskeyLoginCompleteBody {
  credential: Record<string, unknown>;
}

/** `POST /api/v1/auth/passkeys/login/complete` — PRE-session. One generic
 * `ApiError(status: 401)` for every rejection reason (unknown credential,
 * expired/replayed challenge, bad signature, sign-count regression,
 * inactive user) — never distinguishable by the caller, same
 * no-enumeration contract as `postLogin`. Mints a fresh session cookie on
 * success. */
export function postPasskeyLoginComplete(body: PasskeyLoginCompleteBody): Promise<PasskeyLoginOut> {
  return request<PasskeyLoginOut>("/auth/passkeys/login/complete", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** `GET /api/v1/auth/passkeys` — session-authed; ONLY the caller's own
 * passkeys (id/nickname/created/last-used — never the public key or
 * credential id bytes). Powers the Settings "Your sign-in" list (UX §4.2). */
export function fetchPasskeys(): Promise<PasskeyOut[]> {
  return request<PasskeyOut[]>("/auth/passkeys");
}

/** `DELETE /api/v1/auth/passkeys/{id}` — session-authed, self-serve only
 * (no admin path this sprint — ADR-002 A4-R5, logged as a fast-follow in
 * `debt.md`). A 404 means "not found OR not yours," identically, never a
 * 403 that would confirm someone else's passkey id. */
export function deletePasskey(passkeyId: string): Promise<PasskeyDeleteOut> {
  return request<PasskeyDeleteOut>(`/auth/passkeys/${passkeyId}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Sprint-003 A6 — Team tab + hand-out-work (delegation)
// ---------------------------------------------------------------------------

export function fetchTeamBoard(date?: string): Promise<TeamBoardOut> {
  const query = date ? `?date=${encodeURIComponent(date)}` : "";
  return request<TeamBoardOut>(`/team/board${query}`);
}

export function fetchAssignments(scope: "mine" | "team"): Promise<AssignmentOut[]> {
  return request<AssignmentOut[]>(`/assignments?scope=${scope}`);
}

export interface CreateAssignmentBody {
  idempotency_key: string;
  title: string;
  assignee_id?: string;
  due_date?: string | null;
  notes?: string | null;
}

export function createAssignment(body: CreateAssignmentBody): Promise<AssignmentOut> {
  return request<AssignmentOut>("/assignments", { method: "POST", body: JSON.stringify(body) });
}

export interface SplitAssignmentBody {
  idempotency_key: string;
  pieces: AssignmentSplitPieceIn[];
}

export function splitAssignment(
  assignmentId: string,
  body: SplitAssignmentBody,
): Promise<AssignmentSplitOut> {
  return request<AssignmentSplitOut>(`/assignments/${assignmentId}/split`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteAssignment(
  assignmentId: string,
  idempotencyKey: string,
): Promise<AssignmentDeleteOut> {
  return request<AssignmentDeleteOut>(`/assignments/${assignmentId}`, {
    method: "DELETE",
    body: JSON.stringify({ idempotency_key: idempotencyKey }),
  });
}

export interface PatchAssignmentBody {
  idempotency_key: string;
  title?: string;
  assignee_id?: string;
  due_date?: string | null;
  notes?: string | null;
}

/**
 * `PATCH /api/v1/assignments/{id}` — admin/manager/api only. Sprint-009 B4's
 * calendar reuses this for §2.8's "Reschedule a one-off / dated assignment"
 * write action (ADR-002 Amendment 5 decision 3(b)) — a plain `due_date`
 * change through this already-idempotent, already-reversible endpoint (its
 * own "reversal" is simply another PATCH restoring the prior value — no
 * dedicated undo endpoint, per ADR-002 §8).
 */
export function patchAssignment(
  assignmentId: string,
  body: PatchAssignmentBody,
): Promise<AssignmentOut> {
  return request<AssignmentOut>(`/assignments/${assignmentId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export interface PostCompletionBody {
  cadence_id: string;
  completed_by: string;
  idempotency_key: string;
  completed_at?: string;
  note?: string;
}

export function postCompletion(body: PostCompletionBody): Promise<CompletionOut> {
  return request<CompletionOut>("/completions", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface AssignmentMutationBody {
  idempotency_key: string;
  completed_at?: string;
}

export function completeAssignment(
  assignmentId: string,
  body: AssignmentMutationBody,
): Promise<AssignmentOut> {
  return request<AssignmentOut>(`/assignments/${assignmentId}/complete`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function uncompleteAssignment(
  assignmentId: string,
  body: { idempotency_key: string },
): Promise<AssignmentOut> {
  return request<AssignmentOut>(`/assignments/${assignmentId}/uncomplete`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * `DELETE /api/v1/completions/{id}` — undoes a cadence-backed completion
 * (the reversal counterpart to `postCompletion`, closing the S6 E2E gap:
 * this call used to 404/405 against every server build before it, and
 * `useDue.ts` still catches non-network errors defensively and rolls the
 * optimistic UI back to "done" rather than leave the UI lying about
 * server state — see `useDue.ts`'s `undoTask()`).
 *
 * Idempotent on `idempotencyKey`, same replay contract as every other
 * mutation in this file: replaying the same key, or undoing a completion
 * id an earlier call already undid, returns the current state (HTTP 200,
 * `replayed: true`) rather than an error.
 */
export function undoCadenceCompletion(
  completionId: string,
  idempotencyKey: string,
): Promise<CompletionUndoOut> {
  return request<CompletionUndoOut>(`/completions/${completionId}`, {
    method: "DELETE",
    body: JSON.stringify({ idempotency_key: idempotencyKey }),
  });
}

// ---------------------------------------------------------------------------
// Sprint-004 B4 — Admin console: people (GET/POST/PATCH /users, magic
// links). Admin/api only server-side (`app/api/v1/users.py`,
// `app/api/v1/magic_links.py`) — a 403 here is exactly how
// `hooks/useIsAdmin.ts` distinguishes an admin from a manager,
// since `GET /api/v1/me` carries no explicit admin signal (flagged in the
// sprint-004 B4/B5 build notes for Solution Architect awareness).
// ---------------------------------------------------------------------------

export function fetchUsers(): Promise<UserAuthStatusOut[]> {
  return request<UserAuthStatusOut[]>("/users");
}

export interface CreateUserBody {
  idempotency_key: string;
  name: string;
  email?: string | null;
  can_hand_out_work?: boolean;
}

export function createUser(body: CreateUserBody): Promise<UserOut> {
  return request<UserOut>("/users", { method: "POST", body: JSON.stringify(body) });
}

export interface PatchUserBody {
  idempotency_key: string;
  name?: string;
  email?: string | null;
  can_hand_out_work?: boolean;
  active?: boolean;
}

export function patchUser(userId: string, body: PatchUserBody): Promise<UserOut> {
  return request<UserOut>(`/users/${userId}`, { method: "PATCH", body: JSON.stringify(body) });
}

/** ADR-007 / security-review-admin-add-admin-design-2026-07-12.md —
 * `POST /api/v1/users/{id}/promote-admin`. Admin-session only (a manager
 * session gets a 403 from the server — this is not re-checked client-side
 * beyond the existing `PersonDetailOverlay`/`AdminView` admin gate). Same
 * idempotency-key replay contract as `createUser`/`patchUser` — the caller
 * generates a fresh key per attempt, mirroring every other mutation in
 * `hooks/useAdminUsers.ts`. Throws `ApiError(status: 409)` for a disabled
 * target (re-enable first) — the caller decides how to surface that.
 */
export interface PromoteAdminBody {
  idempotency_key: string;
}

export function promoteAdmin(userId: string, body: PromoteAdminBody): Promise<UserOut> {
  return request<UserOut>(`/users/${userId}/promote-admin`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * TD-058 / ADR-002 Amendment 12 §2.3 — `POST /api/v1/users/{id}/password`.
 * Sets a password FOR SOMEONE ELSE, forces them to change it before they can
 * reach any other endpoint (server-enforced, §1), and atomically revokes
 * every live link and every live session they hold.
 *
 * The plaintext travels in the request BODY only — never a path segment,
 * never a query string (those land in access logs, proxy logs, browser
 * history, and `Referer` headers). The caller must never persist it: no
 * `localStorage`, no `IndexedDB`, no route param, cleared on unmount.
 *
 * Admin SESSION only — deliberately not reachable by the `api` bearer
 * (§2.3: a leaked API_TOKEN must not be a credential-minting backdoor).
 *
 * Throws `ApiError(status: 409)` for a self-target, a disabled target, and a
 * target who has never finished first-time setup (no credentials row — send
 * them a link instead); `422` when the password is outside 8-256 characters;
 * `429` when the dedicated 6/min bucket trips.
 */
export interface AdminSetPasswordBody {
  password: string;
}

export function setUserPassword(
  userId: string,
  body: AdminSetPasswordBody,
): Promise<AdminSetPasswordOut> {
  return request<AdminSetPasswordOut>(`/users/${userId}/password`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function issueMagicLink(userId: string, ttlMinutes?: number): Promise<MagicLinkIssueOut> {
  return request<MagicLinkIssueOut>(`/users/${userId}/magic-links`, {
    method: "POST",
    body: JSON.stringify(ttlMinutes ? { ttl_minutes: ttlMinutes } : {}),
  });
}

export function reissueMagicLink(userId: string, ttlMinutes?: number): Promise<MagicLinkIssueOut> {
  return request<MagicLinkIssueOut>(`/users/${userId}/magic-links/reissue`, {
    method: "POST",
    body: JSON.stringify(ttlMinutes ? { ttl_minutes: ttlMinutes } : {}),
  });
}

export function revokeMagicLink(userId: string, linkId: string): Promise<MagicLinkRevokeOut> {
  return request<MagicLinkRevokeOut>(`/users/${userId}/magic-links/${linkId}`, {
    method: "DELETE",
  });
}

/**
 * `GET /api/v1/users/{id}/magic-links` — sprint-004 B6 gap-fix. Every
 * link this person has ever held (live, expired, revoked alike), newest
 * first — never a raw token. Lets `useAdminUsers.stopLink` target every
 * currently-LIVE link precisely, replacing the earlier same-session-only
 * `linkIdByUser` cache this hook used to depend on.
 */
export function fetchUserMagicLinks(userId: string): Promise<MagicLinkListItemOut[]> {
  return request<MagicLinkListItemOut[]>(`/users/${userId}/magic-links`);
}

// ---------------------------------------------------------------------------
// Sprint-004 B4 — Admin console: chores (cadence registry CRUD). Admin/api
// only for create/patch (`app/api/v1/cadences.py`); admin/manager/api may
// read (list + preview), but the Admin tab itself is admin-only (see
// `hooks/useIsAdmin.ts`), so every caller here is already known-admin.
// ---------------------------------------------------------------------------

export function fetchCadences(): Promise<CadenceOut[]> {
  return request<CadenceOut[]>("/cadences");
}

export interface CreateCadenceBody {
  idempotency_key: string;
  task: string;
  area?: string | null;
  cadence_type: CadenceTypeValue;
  interval_days?: number | null;
  schedule_detail?: CadenceScheduleDetail | null;
  default_assignee_id?: string | null;
  notes?: string | null;
}

export function createCadence(body: CreateCadenceBody): Promise<CadenceOut> {
  return request<CadenceOut>("/cadences", { method: "POST", body: JSON.stringify(body) });
}

export interface PatchCadenceBody {
  idempotency_key: string;
  task?: string;
  area?: string | null;
  notes?: string | null;
  default_assignee_id?: string | null;
  interval_days?: number | null;
  schedule_detail?: CadenceScheduleDetail | null;
  paused?: boolean;
}

export function patchCadence(cadenceId: string, body: PatchCadenceBody): Promise<CadenceOut> {
  return request<CadenceOut>(`/cadences/${cadenceId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function previewCadence(cadenceId: string, date?: string): Promise<CadencePreviewOut> {
  const query = date ? `?date=${encodeURIComponent(date)}` : "";
  return request<CadencePreviewOut>(`/cadences/${cadenceId}/preview${query}`);
}

// ---------------------------------------------------------------------------
// Sprint-005 C4 — Animals ("More" tab). Reads are open to every authed role
// server-side (`app/api/v1/animals.py`); `POST .../health-events` is
// admin/api only — the UI only ever calls it for the admin (see
// `views/AnimalDetailOverlay.tsx`'s `isAdmin` gate), but the 403 a
// member/manager would get if this were ever called is the real
// enforcement boundary, not the UI hiding the button.
// ---------------------------------------------------------------------------

export function fetchAnimals(): Promise<AnimalOut[]> {
  return request<AnimalOut[]>("/animals");
}

export interface CreateAnimalBody {
  idempotency_key: string;
  name: string;
  species?: string | null;
  birth_date?: string | null;
  paddock?: string | null;
}

/**
 * `POST /api/v1/animals` — admin/api only (sprint-013 A1/C1, SA ruling
 * sa-ruling-crud-endpoints-2026-07-10.md Decision 1.3). `name` is the one
 * required field; `species`/`birth_date`/`paddock` are optional. `active`
 * is never sent — the server always creates `active: true`. Idempotent:
 * replaying the same `idempotency_key` returns the ORIGINAL row unchanged
 * (`replayed: true`), never a duplicate insert. 409 on a name collision —
 * see `lib/recordsCopy.ts::animalNameConflictMessage` for the caller's
 * inline error copy.
 */
export function createAnimal(body: CreateAnimalBody): Promise<AnimalCreateOut> {
  return request<AnimalCreateOut>("/animals", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function fetchHealthEvents(animalId: string): Promise<HealthEventOut[]> {
  return request<HealthEventOut[]>(`/animals/${animalId}/health-events`);
}

export interface LogHealthEventBody {
  idempotency_key: string;
  note: string;
  event_type?: HealthEventTypeValue | null;
  event_date?: string | null;
}

export function logHealthEvent(
  animalId: string,
  body: LogHealthEventBody,
): Promise<HealthEventOut> {
  return request<HealthEventOut>(`/animals/${animalId}/health-events`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface PatchAnimalBody {
  idempotency_key: string;
  name?: string;
  species?: string | null;
  birth_date?: string | null;
  paddock?: string | null;
  notes?: string | null;
  active?: boolean;
}

/**
 * `PATCH /api/v1/animals/{id}` — admin/api only (ADR-002 A6 decision 7.1).
 * Partial update — presence-based: a field entirely omitted from the body
 * is left unchanged server-side. Sprint-010 C4 (design-phase-c.md §2.3)
 * added the Overview tab's inline notes edit (`notes` only). Sprint-013 A1/
 * C1 (SA ruling Decision 1.4) widens this to `name`/`species`/`birth_date`/
 * `paddock` (the Add/Edit form-sheet's fields) and `active` (the Archive
 * flow — `active: false`; no separate archive endpoint exists for
 * animals). Required `idempotency_key`, same convention as every other
 * mutation (Decision 7.4, R-C0b-2).
 */
export function patchAnimal(animalId: string, body: PatchAnimalBody): Promise<AnimalPatchOut> {
  return request<AnimalPatchOut>(`/animals/${animalId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export interface SetPrimaryPhotoBody {
  idempotency_key: string;
  /** The attachment to set as this animal's card photo — must belong to
   * this animal and be a photo (else a 422 the caller should treat as a
   * generic save failure; this UI never offers a mismatched attachment
   * id). `null` clears the explicit selection, reverting resolution to
   * the newest photo attachment (SA ruling Decision 4.2). A plain
   * required field, not presence-based — `PUT` always states its full
   * intent. */
  attachment_id: string | null;
}

/**
 * `PUT /api/v1/animals/{id}/primary-photo` — admin/api only (sprint-013
 * B1/B2, SA ruling Decision 4.4). Sets/clears this animal's explicit
 * card-photo selection; see `hooks/useAnimals.ts::useAnimals().setPrimaryPhoto`
 * for the caller that patches the fetched roster in place (the same
 * `patchNotes`-style TD-010 pattern, never a refetch). This codebase's
 * first `PUT` — chosen because setting a single sub-resource pointer is
 * idempotent-by-nature (a repeat set is a no-op replay).
 */
export function setAnimalPrimaryPhoto(
  animalId: string,
  body: SetPrimaryPhotoBody,
): Promise<SetPrimaryPhotoOut> {
  return request<SetPrimaryPhotoOut>(`/animals/${animalId}/primary-photo`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export interface PatchHealthEventBody {
  idempotency_key: string;
  note?: string;
  event_type?: HealthEventTypeValue | null;
  event_date?: string | null;
}

/**
 * `PATCH /api/v1/animals/{id}/health-events/{event_id}` — admin/api only.
 * Sprint-010 C4 (design-phase-c.md §2.4) — the Care & health tab's
 * per-item edit sheet. Editable: note/event_type/event_date only.
 */
export function patchHealthEvent(
  animalId: string,
  healthEventId: string,
  body: PatchHealthEventBody,
): Promise<HealthEventOut> {
  return request<HealthEventOut>(`/animals/${animalId}/health-events/${healthEventId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Sprint-010 C3 (ADR-002 Amendment 6 decision 6) — an animal's weights.
// `GET` is open to every authed role; `POST`/`DELETE` are admin/api only
// server-side (`app/api/v1/animals.py`) — the UI only ever calls the
// write pair from `AnimalDetailOverlay`'s Weights tab while `isAdmin` is
// true (see `hooks/useAnimals.ts::useAnimalWeights`), but the 403 a
// member/manager would get is the real enforcement boundary.
// ---------------------------------------------------------------------------

export function fetchAnimalWeights(animalId: string): Promise<AnimalWeightOut[]> {
  return request<AnimalWeightOut[]>(`/animals/${animalId}/weights`);
}

export interface LogAnimalWeightBody {
  idempotency_key: string;
  weight_lb: number;
  weighed_on?: string | null;
  note?: string | null;
}

export function logAnimalWeight(
  animalId: string,
  body: LogAnimalWeightBody,
): Promise<AnimalWeightOut> {
  return request<AnimalWeightOut>(`/animals/${animalId}/weights`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteAnimalWeight(
  animalId: string,
  weightId: string,
  idempotencyKey: string,
): Promise<AnimalWeightDeleteOut> {
  return request<AnimalWeightDeleteOut>(`/animals/${animalId}/weights/${weightId}`, {
    method: "DELETE",
    body: JSON.stringify({ idempotency_key: idempotencyKey }),
  });
}

// ---------------------------------------------------------------------------
// Sprint-010 C3 (ADR-002 Amendment 6 decisions 2-4) — an animal's photos
// ("attachments" server-side; never that word in UI copy, see
// `lib/recordsCopy.ts`). `GET` (list/display/thumb) is open to every
// authed role; `POST`/`DELETE` are admin/api only. Every image byte is
// app-proxied — `attachmentUrl`/`attachmentThumbUrl` below build
// same-origin relative paths for a plain `<img src>`, never a direct
// MinIO URL (ADR-002 A6 decision 2 — MinIO is never reachable from the
// browser). The session cookie rides along automatically on a same-origin
// `<img>` request, no extra wiring needed.
// ---------------------------------------------------------------------------

export function fetchAnimalAttachments(animalId: string): Promise<AttachmentOut[]> {
  return request<AttachmentOut[]>(`/animals/${animalId}/attachments`);
}

/** Same-origin path for an attachment's full/display-size image — pass
 * directly as an `<img src>`. Never fetched through `request()` (not
 * JSON). */
export function attachmentUrl(attachmentId: string): string {
  return `${API_BASE}/attachments/${attachmentId}`;
}

/** Same-origin path for an attachment's <=400px thumbnail — the Photos
 * grid's own `<img src>`. */
export function attachmentThumbUrl(attachmentId: string): string {
  return `${API_BASE}/attachments/${attachmentId}/thumb`;
}

/**
 * `POST /api/v1/animals/{id}/attachments` — multipart/form-data, so this
 * bypasses `request()`'s JSON `Content-Type` (the browser sets its own
 * `multipart/form-data; boundary=...` when given a `FormData` body — an
 * explicit `Content-Type` header here would omit that boundary and break
 * the upload). The CSRF header still applies (an unsafe, cookie-authed
 * mutation) — mirrors `request()`'s own convention rather than
 * duplicating a second CSRF constant.
 */
export async function uploadAttachment(
  animalId: string,
  file: File,
  idempotencyKey: string,
): Promise<AttachmentOut> {
  const form = new FormData();
  form.append("idempotency_key", idempotencyKey);
  form.append("file", file);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/animals/${animalId}/attachments`, {
      method: "POST",
      headers: { [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE },
      body: form,
    });
  } catch (err) {
    throw new ApiError(err instanceof Error ? err.message : "network error", 0, true);
  }

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    const code = parseDetailCode(body);
    noteHttpError(response.status, code);
    throw new ApiError(`${response.status} ${body}`.trim(), response.status, false, code);
  }
  return (await response.json()) as AttachmentOut;
}

export function deleteAttachment(
  attachmentId: string,
  idempotencyKey: string,
): Promise<AttachmentDeleteOut> {
  return request<AttachmentDeleteOut>(`/attachments/${attachmentId}`, {
    method: "DELETE",
    body: JSON.stringify({ idempotency_key: idempotencyKey }),
  });
}

// ---------------------------------------------------------------------------
// Sprint-005 C4 — Projects ("More" tab). `GET /projects*` is admin/manager/
// api — a member gets a 403 (ADR-002 §5/§11 grants no member-level "view
// projects" cell at all), so `hooks/useProjects.ts` never calls this for a
// member in the first place. `GET .../bids` is admin/api ONLY (never
// manager) — `hooks/useProjectBids.ts`'s `enabled` gate ensures this is
// never even requested for a non-admin, not just hidden once the response
// arrives (structural gate, matching `ProjectOut`'s own "no bid field,
// ever" guarantee on the server).
//
// Sprint-013 A2/C2 (design-spec-animal-cards-2026-07-10.md §5, SA ruling
// sa-ruling-crud-endpoints-2026-07-10.md Decision 2) adds create/archive/
// restore + widens the patch to `name` — see each function's own
// docstring below.
// ---------------------------------------------------------------------------

/**
 * `GET /api/v1/projects` — admin/manager/api. `includeArchived` (default
 * `false`) maps to the server's own `?include_archived` query param
 * (sprint-013 A2, SA ruling Decision 2.5) — `hooks/useProjects.ts` always
 * fetches with `true` so the Active/All toggle can filter client-side with
 * no second request, the same "fetch everything once, filter locally"
 * shape `fetchAnimals()`/`AnimalsView`'s toggle already established (E1
 * ruling) — archived projects are simply never excluded at the fetch
 * layer here, only at render time.
 */
export function fetchProjects(includeArchived = false): Promise<ProjectOut[]> {
  const query = includeArchived ? "?include_archived=true" : "";
  return request<ProjectOut[]>(`/projects${query}`);
}

export function fetchProjectBids(projectId: string): Promise<BidOut[]> {
  return request<BidOut[]>(`/projects/${projectId}/bids`);
}

export interface CreateProjectBody {
  idempotency_key: string;
  name: string;
  status?: string | null;
  summary?: string | null;
}

/**
 * `POST /api/v1/projects` — admin/api only (sprint-013 A2/C2, SA ruling
 * Decision 2.3). `name` is the one required field; `status`/`summary` are
 * optional and freeform (no enum/closed set, same posture as
 * `createAnimal`). `punch_list`/`archived_at` are never sent — every new
 * project is born empty/not-archived. Idempotent: replaying the same
 * `idempotency_key` returns the ORIGINAL row unchanged (`replayed: true`),
 * never a duplicate insert. 409 on a name collision — see
 * `lib/recordsCopy.ts::projectNameConflictMessage` for the caller's
 * inline error copy.
 */
export function createProject(body: CreateProjectBody): Promise<ProjectCreateOut> {
  return request<ProjectCreateOut>("/projects", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface PatchProjectBody {
  idempotency_key: string;
  name?: string;
  summary?: string | null;
  status?: string | null;
}

/**
 * `PATCH /api/v1/projects/{id}` — admin/api only (ADR-002 A6 decision
 * 7.1 — stricter than manager's read access to the project itself).
 * Sprint-010 C4 (design-phase-c.md §3.1) — originally summary/status
 * only; sprint-013 A2/C2 (SA ruling Decision 2.5) widens this to `name`
 * (rename, 409 on collision with a DIFFERENT project's name, never
 * clearable to null). `status` is confirmed free text (A6-R6, ratified)
 * — never validated against a closed enum here. `archived_at` is never
 * accepted here — see `archiveProject`/`restoreProject` below.
 */
export function patchProject(projectId: string, body: PatchProjectBody): Promise<ProjectPatchOut> {
  return request<ProjectPatchOut>(`/projects/${projectId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/**
 * `DELETE /api/v1/projects/{id}` — admin/api only (sprint-013 A2/C2, SA
 * ruling Decision 2.4). Soft-delete: sets `archived_at` rather than
 * removing the row — no dedicated archive endpoint exists for animals
 * (they reuse `PATCH .../active`), but projects get their own, matching
 * the SA ruling's per-entity decision. DELETE-with-body, same convention
 * as `deleteAttachment`/`deleteAnimalWeight` above. Idempotent: replaying
 * the same `idempotency_key`, or archiving an already-archived project,
 * both return HTTP 200 (TD-028 — a replay reflects the row's ACTUAL
 * current state, which can be non-null-then-null if an intervening
 * restore happened).
 */
export function archiveProject(
  projectId: string,
  idempotencyKey: string,
): Promise<ProjectArchiveOut> {
  return request<ProjectArchiveOut>(`/projects/${projectId}`, {
    method: "DELETE",
    body: JSON.stringify({ idempotency_key: idempotencyKey }),
  });
}

/**
 * `POST /api/v1/projects/{id}/restore` — admin/api only (sprint-013 A2/C2,
 * SA ruling Decision 2.4). Clears `archived_at` back to `null` — a
 * dedicated endpoint, not a `PATCH` of `archived_at`. Idempotent: replaying
 * the same `idempotency_key`, or restoring an already-active project, both
 * return HTTP 200.
 */
export function restoreProject(
  projectId: string,
  idempotencyKey: string,
): Promise<ProjectRestoreOut> {
  return request<ProjectRestoreOut>(`/projects/${projectId}/restore`, {
    method: "POST",
    body: JSON.stringify({ idempotency_key: idempotencyKey }),
  });
}

// ---------------------------------------------------------------------------
// Sprint-017 E7 — project tasks, project shares, and the DE-IDENTIFIED
// assignee feed (ADR-003 §4/§5). Object-level authz is enforced server-side
// per handler (`can_read_project`/`can_edit_tasks`/`can_assign_out`/
// `can_share`); the client draws an affordance only where the predicate
// allows (design §6), so these are never called for a role that would 403.
// The `/me/project-tasks*` pair is the de-identified path — it returns/
// accepts NO project identity (P0 privacy boundary, ADR-003 §4.4).
// ---------------------------------------------------------------------------

/** `GET /projects/{id}/tasks` — the full, project-scoped task list. Auth:
 * `can_read_project` (owner / collaborator / admin-api). */
export function fetchProjectTasks(projectId: string): Promise<ProjectTaskOut[]> {
  return request<ProjectTaskOut[]>(`/projects/${projectId}/tasks`);
}

export interface CreateProjectTaskBody {
  idempotency_key: string;
  title: string;
  safety?: boolean;
  position?: number | null;
  assignee_id?: string | null;
}

/** `POST /projects/{id}/tasks` — add a task. Auth: `can_edit_tasks`;
 * supplying `assignee_id` additionally requires `can_assign_out`. */
export function createProjectTask(
  projectId: string,
  body: CreateProjectTaskBody,
): Promise<ProjectTaskCreateOut> {
  return request<ProjectTaskCreateOut>(`/projects/${projectId}/tasks`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface PatchProjectTaskBody {
  idempotency_key: string;
  title?: string;
  position?: number;
  done?: boolean;
  assignee_id?: string | null;
  safety?: boolean;
}

/** `PATCH /projects/{id}/tasks/{task_id}` — rename / reorder / toggle /
 * (re)assign. Partial (presence-based). A present `assignee_id` (assign,
 * reassign, OR clear→null) requires `can_assign_out` server-side. */
export function patchProjectTask(
  projectId: string,
  taskId: string,
  body: PatchProjectTaskBody,
): Promise<ProjectTaskPatchOut> {
  return request<ProjectTaskPatchOut>(`/projects/${projectId}/tasks/${taskId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** `DELETE /projects/{id}/tasks/{task_id}` — soft-archive a task. Auth:
 * `can_edit_tasks`. Idempotent. */
export function deleteProjectTask(
  projectId: string,
  taskId: string,
  idempotencyKey: string,
): Promise<ProjectTaskArchiveOut> {
  return request<ProjectTaskArchiveOut>(`/projects/${projectId}/tasks/${taskId}`, {
    method: "DELETE",
    body: JSON.stringify({ idempotency_key: idempotencyKey }),
  });
}

/** `GET /projects/{id}/shares` — the collaborator roster. Auth: `can_share`
 * (owner + admin/api); a reachable non-owner gets 403. */
export function fetchProjectShares(projectId: string): Promise<ProjectShareOut[]> {
  return request<ProjectShareOut[]>(`/projects/${projectId}/shares`);
}

/** `POST /projects/{id}/shares` — let someone help. Idempotent (re-adding an
 * existing collaborator returns `replayed: true`, no duplicate row). */
export function createProjectShare(
  projectId: string,
  userId: string,
  idempotencyKey: string,
): Promise<ProjectShareCreateOut> {
  return request<ProjectShareCreateOut>(`/projects/${projectId}/shares`, {
    method: "POST",
    body: JSON.stringify({ idempotency_key: idempotencyKey, user_id: userId }),
  });
}

/** `DELETE /projects/{id}/shares/{user_id}` — take someone off (owner) OR
 * leave the project (self). Idempotent. */
export function deleteProjectShare(
  projectId: string,
  userId: string,
): Promise<ProjectShareDeleteOut> {
  return request<ProjectShareDeleteOut>(`/projects/${projectId}/shares/${userId}`, {
    method: "DELETE",
  });
}

/** `GET /me/project-tasks` — the DE-IDENTIFIED assignee feed. Five fields
 * only, NEVER project identity. Merged inline into the caller's Today / My
 * list at the presentation layer (never through the /due engine). */
export function fetchMyProjectTasks(): Promise<MyProjectTaskOut[]> {
  return request<MyProjectTaskOut[]>("/me/project-tasks");
}

/** `PATCH /me/project-tasks/{task_id}` — toggle `done` on my own assigned
 * task. Never requires or exposes `project_id`. 404 if not mine. */
export function patchMyProjectTask(
  taskId: string,
  done: boolean,
  idempotencyKey: string,
): Promise<MyProjectTaskOut> {
  return request<MyProjectTaskOut>(`/me/project-tasks/${taskId}`, {
    method: "PATCH",
    body: JSON.stringify({ idempotency_key: idempotencyKey, done }),
  });
}

/** `GET /me/shareable-users` (sprint-018 E4/TD-044) — the member-owner
 * "Who's helping" picker's candidate list: every ACTIVE user except the
 * caller, `{id, name}` only. Session-cookie auth. Only ever called for a
 * plain member who owns the project being shared — a manager/admin session
 * keeps sourcing its picker from the existing team-board roster instead
 * (see `WhosHelping.tsx`'s `isMemberOwner` gate). */
export function fetchShareableUsers(): Promise<ShareableUserOut[]> {
  return request<ShareableUserOut[]>("/me/shareable-users");
}

// ---------------------------------------------------------------------------
// Sprint-010 C4 (ADR-002 Amendment 6 decision 8) — the contact directory
// (vets/farriers/vendors). admin/manager/api read-only — a member is
// denied (403), matching `canHandOutWork`'s existing gate everywhere else
// (Decision 8.2). Sorted name-ascending client-side (Decision 8.1 — the
// endpoint carries no guaranteed server order).
//
// Sprint-013 A3/C3 (design-spec-animal-cards-2026-07-10.md §5, SA ruling
// sa-ruling-crud-endpoints-2026-07-10.md Decision 3, ADR-002 Amendment 8)
// adds create/patch/archive/restore + widens the fetch with
// `?include_archived`, mirroring `fetchProjects`/`createProject`/
// `patchProject`/`archiveProject`/`restoreProject` above field-for-field.
// ---------------------------------------------------------------------------

/**
 * `GET /api/v1/contacts` — admin/manager/api. `includeArchived` (default
 * `false`) maps to the server's own `?include_archived` query param
 * (sprint-013 A3, SA ruling Decision 3.4) — `hooks/useContacts.ts` always
 * fetches with `true` so the Active/All toggle can filter client-side with
 * no second request, same "fetch everything once, filter locally" shape as
 * `fetchProjects`'s own `includeArchived` param.
 */
export function fetchContacts(includeArchived = false): Promise<ContactOut[]> {
  const query = includeArchived ? "?include_archived=true" : "";
  return request<ContactOut[]>(`/contacts${query}`);
}

export interface CreateContactBody {
  idempotency_key: string;
  name: string;
  contact?: string | null;
  notes?: string | null;
}

/**
 * `POST /api/v1/contacts` — admin/api only (sprint-013 A3/C3, SA ruling
 * Decision 3.4). `name` is the one required field; `contact`/`notes` are
 * optional. `archived_at` is never sent — every new contact is born
 * un-archived. Idempotent: replaying the same `idempotency_key` returns the
 * ORIGINAL row unchanged (`replayed: true`), never a duplicate insert. 409
 * on a name collision — see `lib/recordsCopy.ts::contactNameConflictMessage`
 * for the caller's inline error copy.
 */
export function createContact(body: CreateContactBody): Promise<ContactCreateOut> {
  return request<ContactCreateOut>("/contacts", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface PatchContactBody {
  idempotency_key: string;
  name?: string;
  contact?: string | null;
  notes?: string | null;
}

/**
 * `PATCH /api/v1/contacts/{id}` — admin/api only (SA ruling Decision 3.4).
 * `name`/`contact`/`notes` ONLY — never `archived_at` (its own dedicated
 * `DELETE`/`.../restore` endpoints below). `name` may not clear to null
 * (409 on rename collision with a DIFFERENT contact's existing name);
 * `contact`/`notes` may be explicitly cleared to null.
 */
export function patchContact(contactId: string, body: PatchContactBody): Promise<ContactPatchOut> {
  return request<ContactPatchOut>(`/contacts/${contactId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/**
 * `DELETE /api/v1/contacts/{id}` — admin/api only (SA ruling Decision
 * 3.2/3.4). Soft-delete: sets `archived_at` rather than removing the row —
 * `bids.contractor_id` is a FK to this table, so a hard delete would orphan
 * commercial bid history. DELETE-with-body, same convention as
 * `archiveProject`. Idempotent: replaying the same `idempotency_key`, or
 * archiving an already-archived contact, both return HTTP 200.
 */
export function archiveContact(
  contactId: string,
  idempotencyKey: string,
): Promise<ContactArchiveOut> {
  return request<ContactArchiveOut>(`/contacts/${contactId}`, {
    method: "DELETE",
    body: JSON.stringify({ idempotency_key: idempotencyKey }),
  });
}

/**
 * `POST /api/v1/contacts/{id}/restore` — admin/api only (SA ruling Decision
 * 3.4). Clears `archived_at` back to `null` — a dedicated endpoint, not a
 * `PATCH` of `archived_at`. Idempotent: replaying the same
 * `idempotency_key`, or restoring an already-active contact, both return
 * HTTP 200.
 */
export function restoreContact(
  contactId: string,
  idempotencyKey: string,
): Promise<ContactRestoreOut> {
  return request<ContactRestoreOut>(`/contacts/${contactId}/restore`, {
    method: "POST",
    body: JSON.stringify({ idempotency_key: idempotencyKey }),
  });
}

// ---------------------------------------------------------------------------
// Sprint-005 C6 — reminders (Web Push). Mirrors app/api/v1/push.py /
// app/api/v1/me.py's `/me/reminders` (see README's "Reminders / Web Push"
// section). Self-serve for every authed human — see `hooks/useReminderPrefs.ts`
// for the client-side subscribe/unsubscribe state machine that calls these.
// ---------------------------------------------------------------------------

export function fetchVapidPublicKey(): Promise<VapidPublicKeyOut> {
  return request<VapidPublicKeyOut>("/push/vapid-public-key");
}

/** Self — always the caller's own subscriptions (member/manager/admin
 * alike); used as a fallback lookup when a device's own subscription id
 * isn't on file locally (e.g. localStorage was cleared) — see
 * `hooks/useReminderPrefs.ts::disable`'s docstring. */
export function fetchMyPushSubscriptions(): Promise<PushSubscriptionOut[]> {
  return request<PushSubscriptionOut[]>("/push-subscriptions");
}

export interface CreatePushSubscriptionBody {
  idempotency_key: string;
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

export function createPushSubscription(
  body: CreatePushSubscriptionBody,
): Promise<PushSubscriptionOut> {
  return request<PushSubscriptionOut>("/push-subscriptions", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deletePushSubscription(subscriptionId: string): Promise<PushSubscriptionRevokeOut> {
  return request<PushSubscriptionRevokeOut>(`/push-subscriptions/${subscriptionId}`, {
    method: "DELETE",
  });
}

/** `PATCH /api/v1/me/reminders` — deliberately no `idempotency_key`, per
 * that endpoint's own docstring (a plain boolean SET, not a delta/create). */
export function patchMyReminders(enabled: boolean): Promise<RemindersPrefsOut> {
  return request<RemindersPrefsOut>("/me/reminders", {
    method: "PATCH",
    body: JSON.stringify({ enabled }),
  });
}

/** `POST /api/v1/me/push/test` — pushes a test to the caller's OWN active
 * devices and reports per-device results. 409 = no devices, 429 = too many
 * tests in a minute, 503 = this farm has no reminders keypair. */
export function sendTestPush(): Promise<PushTestOut> {
  return request<PushTestOut>("/me/push/test", { method: "POST" });
}

// ---------------------------------------------------------------------------
// Sprint-009 B1/B4 — Calendar (mirrors app/schemas/schedule.py, ADR-002
// Amendment 5 decisions 1-3). `GET /schedule` is open to every authed role
// (member gets `scope=mine` only, forced server-side — a member passing
// `scope=team` is a 403 + audit denial row); the two `/schedule/exceptions`
// mutations are admin/manager/api only, the identical guard as
// `POST /assignments` (see `hooks/useSchedule.ts` for the role gate this
// mirrors client-side).
// ---------------------------------------------------------------------------

export function fetchSchedule(
  from: string,
  to: string,
  scope?: "mine" | "team",
): Promise<ScheduleResponse> {
  const params = new URLSearchParams({ from, to });
  if (scope) params.set("scope", scope);
  return request<ScheduleResponse>(`/schedule?${params.toString()}`);
}

export interface CreateScheduleExceptionBody {
  idempotency_key: string;
  cadence_id: string;
  occurrence_date: string;
  action: ScheduleExceptionActionValue;
  new_date?: string;
  assignee_id?: string;
}

/** `POST /api/v1/schedule/exceptions` — §2.8's "Move to another day"
 * (`action: "moved"`, requires `new_date`) and "Skip this one"
 * (`action: "suppressed"`) write actions. Rejects 409 when the occurrence
 * is already completed (completion-wins, ADR-002 A5 decision 2.4) — the
 * caller (`hooks/useSchedule.ts`) catches that specifically to show §2.8's
 * inline "That's already done" copy rather than a generic error. */
export function createScheduleException(
  body: CreateScheduleExceptionBody,
): Promise<ScheduleExceptionOutFull> {
  return request<ScheduleExceptionOutFull>("/schedule/exceptions", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** `DELETE /api/v1/schedule/exceptions` — §2.9's Undo. Addressed by the
 * natural `(cadence_id, occurrence_date)` key, no exposed exception id
 * needed (ADR-002 A5 decision 2.5). Rejects 409 when the linked one-off
 * has itself already been completed. */
export function deleteScheduleException(
  cadenceId: string,
  occurrenceDate: string,
  idempotencyKey: string,
): Promise<ScheduleExceptionDeleteOut> {
  return request<ScheduleExceptionDeleteOut>("/schedule/exceptions", {
    method: "DELETE",
    body: JSON.stringify({
      idempotency_key: idempotencyKey,
      cadence_id: cadenceId,
      occurrence_date: occurrenceDate,
    }),
  });
}

// ---------------------------------------------------------------------------
// Sprint-009 B5 — weather (ADR-002 Amendment 5 decision 4). Cache-only,
// all authenticated roles (admin/manager/member/api) — `require_role` is
// not even applied server-side beyond "any valid session," so no client-
// side gate is needed either (design-phase-b.md §3.1).
// ---------------------------------------------------------------------------

export function fetchWeather(): Promise<WeatherOut> {
  return request<WeatherOut>("/weather");
}

// ---------------------------------------------------------------------------
// Sprint-009 B3 — admin activity feed (ADR-002 Amendment 5 decision 5).
// admin/api only server-side; `hooks/useActivity.ts`'s `enabled` gate
// ensures a non-admin session never issues this request in the first
// place, not just hides its result (same posture `useProjects`/
// `useActivity`-shaped hooks already establish elsewhere in this file).
// ---------------------------------------------------------------------------

export function fetchActivity(limit?: number, before?: string | null): Promise<ActivityResponse> {
  const params = new URLSearchParams();
  if (limit) params.set("limit", String(limit));
  if (before) params.set("before", before);
  const query = params.toString();
  return request<ActivityResponse>(`/activity${query ? `?${query}` : ""}`);
}

// ---------------------------------------------------------------------------
// Sprint-009 B4 — canned reports (ADR-002 Amendment 5 decision 6).
// admin/manager/api server-side; `hooks/useReports.ts`'s `enabled` gate
// (same `canHandOutWork` flag `useProjects` already uses) ensures a
// member session never issues any of these four requests.
// ---------------------------------------------------------------------------

export function fetchCompletionRateReport(
  from: string,
  to: string,
): Promise<CompletionRateResponse> {
  const params = new URLSearchParams({ from, to });
  return request<CompletionRateResponse>(`/reports/completions?${params.toString()}`);
}

/** `GET /api/v1/reports/overdue` — genuinely point-in-time (ADR-002
 * Amendment 5 decision 6 #2, ruled for design-phase-b.md §10.2): takes NO
 * `from`/`to` at all, unlike the other three reports. */
export function fetchOverdueAgingReport(): Promise<OverdueAgingResponse> {
  return request<OverdueAgingResponse>("/reports/overdue");
}

export function fetchHealthLogReport(
  from: string,
  to: string,
  animalId?: string | null,
): Promise<HealthLogResponse> {
  const params = new URLSearchParams({ from, to });
  if (animalId) params.set("animal_id", animalId);
  return request<HealthLogResponse>(`/reports/health-log?${params.toString()}`);
}

export function fetchWorkloadReport(from: string, to: string): Promise<WorkloadResponse> {
  const params = new URLSearchParams({ from, to });
  return request<WorkloadResponse>(`/reports/workload?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Sprint-009 TD-004 — admin passkey list + removal (ADR-002 Amendment 5
// decision 7). Admin/api only server-side, mirrors the self-serve
// `fetchPasskeys`/`deletePasskey` pair above but path-scoped by `user_id`
// (`views/PersonDetailOverlay.tsx`'s "Quick sign-in (Face ID or fingerprint)"
// section, admin-only by construction — this overlay never mounts for a
// non-admin session). No-oracle: an unknown `user_id` returns an empty
// list (never a 404); the `DELETE` returns an identical 404 for "not
// found," "already removed," or "belongs to someone else" alike — the
// caller (`hooks/useUserPasskeys.ts`) surfaces every failure with the
// same generic copy, never a distinguishing message (§6.3).
// ---------------------------------------------------------------------------

export function fetchUserPasskeys(userId: string): Promise<PasskeyOut[]> {
  return request<PasskeyOut[]>(`/users/${userId}/passkeys`);
}

export function deleteUserPasskey(userId: string, credentialId: string): Promise<PasskeyDeleteOut> {
  return request<PasskeyDeleteOut>(`/users/${userId}/passkeys/${credentialId}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Sprint-014 B2/C1 (ADR-002 Amendment 9 decision 8) — farm-wide runtime
// settings (the admin Config page, `views/ConfigView.tsx`). admin/api only
// server-side (`app/api/v1/settings.py`) — NOT manager, unlike most of this
// file's other admin/manager splits; `hooks/useIsAdmin.ts` is this page's
// only gate, same posture as `fetchActivity`/`fetchUsers`.
// ---------------------------------------------------------------------------

export function fetchSettings(): Promise<SettingsOut> {
  return request<SettingsOut>("/settings");
}

/** Every field is optional (partial PATCH — a field omitted from the body is
 * left unchanged server-side). Every env-backed field may be explicitly set
 * to `null` to reset it to the env default (`app/schemas/settings.py`'s
 * sprint-014 B2 null-reset decision) — `garden_exists`/`map_base_layer` have
 * no env fallback and 422 on an explicit `null` (server-side field
 * validators), so this app never sends `null` for either. */
export interface SettingsPatchBody {
  farm_display_name?: string | null;
  farm_timezone?: string | null;
  farm_latitude?: number | null;
  farm_longitude?: number | null;
  weather_forecast_days?: number | null;
  weather_refresh_interval_seconds?: number | null;
  weather_stale_after_seconds?: number | null;
  weather_alerts_refresh_interval_seconds?: number | null;
  weather_alerts_stale_after_seconds?: number | null;
  nws_alerts_user_agent?: string | null;
  vapid_subject?: string | null;
  garden_exists?: boolean;
  map_base_layer?: "map" | "satellite";
  thi_config?: string | null;
}

/** `PATCH /api/v1/settings` — admin/api only, CSRF-wired (`request()`'s
 * generic unsafe-method header), `extra="forbid"` server-side (a LOCKED or
 * never-on-page key in the body 422s outright, never silently dropped).
 * Returns the full resolved `SettingsOut` plus `changed_fields` — the caller
 * (`hooks/useSettings.ts`) applies the WHOLE response back into local state
 * (not just the fields it sent), so `meta.source`/`locked` flags and every
 * OTHER section's values stay in sync with the server's single source of
 * truth after any one section's save. */
export function patchSettings(body: SettingsPatchBody): Promise<SettingsPatchOut> {
  return request<SettingsPatchOut>("/settings", { method: "PATCH", body: JSON.stringify(body) });
}
